import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Mic2, Sparkles, Upload, Loader2, Play, Share2, Music2, RefreshCw,
  FileMusic, ArrowRight, ImageIcon, X, ChevronDown, Search, Volume2, Trophy, Flame, Trash2, RotateCw, MicVocal,
} from "lucide-react";

import { api, API_BASE } from "@/lib/api";
import KaraokeModal from "@/components/production/KaraokeModal";
import KaraokeShareModal from "@/components/production/KaraokeShareModal";
import { appConfirm } from "@/components/AppDialogHost";

/**
 * Karaoke Studio — unified landing page for the Studio Karaoke pipeline.
 *
 * Consolidates what was previously scattered across Imaging Studio,
 * Stem Studio, Vocalist Studio, and Creative Studio. This page is the
 * canonical front door: pick any karaoke-ready audio source in the
 * user's library, kick off a render (LALAL.AI stem-split → Whisper
 * word-level transcript → FFmpeg karaoke MP4), preview inline, share
 * to socials.
 *
 * Rendering itself still lives in `KaraokeModal` — this page just
 * hosts the source picker + past-render gallery.
 */

// Which imaging-library `kind` values are audio-based and karaoke-ready.
// Beds, voice takes, jingles, generated mixdowns, imported drops — anything
// with playable audio. We deliberately exclude image/avatar/news_video
// kinds so the picker stays tidy.
const AUDIO_KINDS = new Set([
  "bed", "voice", "jingle", "sfx", "fx", "music", "opener", "closer", "ping",
  "impact", "generated", "mixdown", "ai_generate", "commercial", "podcast",
  "library", "imaging",
]);

// Source-tab filter presets. Each maps to a predicate over an imaging
// asset. Tabs are pure client-side filters so switching is instant.
const SOURCE_TABS = [
  { id: "all",     label: "All Karaoke-Ready", icon: Music2,
    predicate: (a) => AUDIO_KINDS.has(a.kind || "") },
  { id: "vocals",  label: "From Vocalist Studio", icon: Mic2,
    predicate: (a) => a.source === "vocalist" || (a.asset_id || "").startsWith("vocal_") || a.kind === "voice" },
  { id: "stems",   label: "From Stem Studio", icon: Volume2,
    predicate: (a) => a.source === "stem_split" || (a.asset_id || "").startsWith("stem_") },
  { id: "beds",    label: "Beds & FX", icon: FileMusic,
    predicate: (a) => ["bed", "jingle", "sfx", "fx", "opener", "closer", "impact", "ping"].includes(a.kind || "") },
  { id: "imports", label: "Uploads", icon: Upload,
    predicate: (a) => a.source === "production_import" || (a.asset_id || "").startsWith("ps_imp_") },
];


export default function KaraokeStudio() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [assets, setAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [shareJob, setShareJob] = useState(null);   // {job_id, source_label} for the share modal
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [reliability, setReliability] = useState(null);   // Update #157 — 24h reliability chip
  const [uploading, setUploading] = useState(false);
  const uploadInputRef = useRef(null);

  // ── Load user's karaoke-ready audio library ────────────────────
  const loadAssets = useCallback(async () => {
    setLoadingAssets(true);
    try {
      const r = await api.get("/imaging/assets");
      const rows = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      // Only keep audio-ish rows so the picker isn't polluted with
      // avatar / news_video / image_library entries.
      setAssets(rows.filter((a) => AUDIO_KINDS.has(a.kind || "")));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load your audio library");
    } finally {
      setLoadingAssets(false);
    }
  }, []);

  // ── Load past karaoke jobs so users can jump straight to share ──
  const loadJobs = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const r = await api.get("/production/karaoke");
      const rows = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.jobs || []);
      setJobs(rows);
    } catch {
      // list is optional; render gracefully with empty state
      setJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  const loadLeaderboard = useCallback(async () => {
    try {
      const r = await api.get("/production/karaoke/leaderboard?limit=5");
      setLeaderboard(r.data?.items || []);
    } catch {
      setLeaderboard([]);
    }
  }, []);

  // Update #157 — pull the last-24h reliability signal for the header
  // chip. Silent on failure — reliability is a nice-to-have, not a
  // page blocker.
  const loadReliability = useCallback(async () => {
    try {
      const r = await api.get("/production/karaoke/reliability?hours=24");
      setReliability(r.data || null);
    } catch {
      setReliability(null);
    }
  }, []);

  useEffect(() => { loadAssets(); loadJobs(); loadLeaderboard(); loadReliability(); }, [loadAssets, loadJobs, loadLeaderboard, loadReliability]);

  // Delete a karaoke render from the grid (soft-delete on shared/played
  // renders, hard-delete on failed ones — backend picks which). Optimistic
  // update on the client so the tile disappears immediately.
  const deleteJob = useCallback(async (job) => {
    const isFailed = job.status === "failed";
    const msg = isFailed
      ? "Delete this failed render?"
      : "Remove this karaoke render from your library?";
    if (!await appConfirm(msg)) return;
    setJobs((prev) => prev.filter((j) => j.job_id !== job.job_id));
    try {
      await api.delete(`/production/karaoke/${job.job_id}`);
      toast.success(isFailed ? "Failed render removed" : "Karaoke removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
      loadJobs(); // rollback the optimistic hide
    }
  }, [loadJobs]);

  // Retry a failed karaoke render — re-queues the same source through
  // the pipeline. Backend reuses the same job_id so the card just resets
  // from "Failed" → "Rendering…" in place instead of spawning a new tile.
  const retryJob = useCallback(async (job) => {
    // Optimistic: flip the card to "queued" locally so the user gets
    // instant feedback while the background pipeline restarts.
    setJobs((prev) => prev.map((j) => (
      j.job_id === job.job_id
        ? { ...j, status: "queued", progress_step: "queued", error: null }
        : j
    )));
    try {
      await api.post(`/production/karaoke/${job.job_id}/retry`);
      toast.success("Retry queued — this render will restart in a moment");
      // Kick a fresh fetch after a beat so the ticker picks up the
      // pipeline's real progress_step values.
      setTimeout(() => loadJobs(), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
      loadJobs(); // rollback optimistic
    }
  }, [loadJobs]);

  // ── Deep-link support: /dashboard/karaoke-studio?asset=<id> ──
  useEffect(() => {
    const asset_id = searchParams.get("asset");
    if (!asset_id) return;
    if (!assets.length) return;
    const found = assets.find((a) => a.asset_id === asset_id);
    if (found) {
      setSelectedAsset(found);
      const next = new URLSearchParams(searchParams);
      next.delete("asset");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, assets, setSearchParams]);

  // ── Upload → creates an imaging_asset → open KaraokeModal on it ──
  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("track_id", "bed");
      fd.append("label", file.name.replace(/\.[^/.]+$/, ""));
      const r = await api.post("/production/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const asset = r?.data?.asset;
      if (asset?.asset_id) {
        toast.success(`Uploaded "${asset.label}" — opening karaoke render…`);
        // Refresh the grid + auto-open the render modal
        await loadAssets();
        setSelectedAsset(asset);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (uploadInputRef.current) uploadInputRef.current.value = "";
    }
  };

  // Filtered assets by tab + query
  const filteredAssets = useMemo(() => {
    const preset = SOURCE_TABS.find((t) => t.id === tab) || SOURCE_TABS[0];
    const needle = q.trim().toLowerCase();
    return assets
      .filter(preset.predicate)
      .filter((a) => !needle
        || (a.label || "").toLowerCase().includes(needle)
        || (a.name || "").toLowerCase().includes(needle)
        || (a.genre || "").toLowerCase().includes(needle))
      .slice(0, 200);
  }, [assets, tab, q]);

  // Counts per tab (for the tab badges)
  const tabCounts = useMemo(() => {
    const counts = {};
    SOURCE_TABS.forEach((t) => { counts[t.id] = assets.filter(t.predicate).length; });
    return counts;
  }, [assets]);

  return (
    <div data-testid="karaoke-studio-page"
         className="mx-auto max-w-6xl px-6 py-8" style={{ color: "#e6e9f0" }}>
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="type-h1 text-white">Karaoke Studio</div>
            <span data-testid="karaoke-studio-beta-badge"
                  className="rounded-full px-2 py-0.5 type-caption font-semibold uppercase tracking-wide"
                  style={{ background: "rgba(255,138,31,0.15)", color: "#FF8A1F" }}>
              Studio
            </span>
          </div>
          <div className="type-body text-neutral-400 max-w-2xl">
            Turn any track — a bed, a stem-separated vocal, an AI vocalist output, or a fresh upload —
            into a broadcast-ready lyric video. LALAL.AI splits the vocals, Whisper transcribes them
            word-by-word, and we render a Ken-Burns-ready MP4 you can push straight to socials.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={uploadInputRef}
            type="file"
            accept="audio/wav,audio/mpeg,audio/mp3,audio/flac,audio/aac,audio/x-wav,audio/x-flac,audio/x-m4a,audio/mp4"
            className="hidden"
            data-testid="karaoke-upload-input"
            onChange={(e) => onUpload(e.target.files?.[0] || null)}
          />
          <button
            type="button"
            data-testid="karaoke-upload-btn"
            disabled={uploading}
            onClick={() => uploadInputRef.current?.click()}
            className="rounded-full px-4 py-2 font-medium text-white transition disabled:opacity-50"
            style={{ background: "linear-gradient(135deg, #FF8A1F 0%, #a78bfa 100%)" }}
          >
            {uploading
              ? (<><Loader2 className="inline animate-spin mr-2" size={14} /> Uploading…</>)
              : (<><Upload className="inline mr-2" size={14} /> Upload a track</>)}
          </button>
        </div>
      </div>

      {/* ── Karaoke of the Week leaderboard ─────────────────
          Renders only when there's meaningful engagement (≥1 share
          or play) so a fresh station doesn't stare at a chart of
          zeros. Backend already filters to score>0. */}
      {leaderboard.length > 0 && (
        <div data-testid="karaoke-leaderboard"
             className="mb-6 rounded-xl border overflow-hidden"
             style={{
               background: "linear-gradient(135deg, rgba(255,138,31,0.10) 0%, rgba(167,139,250,0.06) 100%)",
               borderColor: "rgba(255,138,31,0.35)",
             }}>
          <div className="flex items-center justify-between px-4 py-3 border-b"
               style={{ borderColor: "rgba(255,138,31,0.20)" }}>
            <div className="flex items-center gap-2">
              <Trophy size={16} style={{ color: "#FF8A1F" }} />
              <div className="text-white" style={{ fontSize: 14, fontWeight: 700 }}>
                Karaoke of the Week
              </div>
              <span className="rounded-full px-2 py-0.5 type-caption font-mono uppercase tracking-wider"
                    style={{ background: "rgba(255,138,31,0.15)", color: "#FF8A1F", fontSize: 10 }}>
                Top {leaderboard.length}
              </span>
            </div>
            <button
              type="button"
              onClick={loadLeaderboard}
              data-testid="karaoke-leaderboard-refresh"
              className="rounded p-1 transition"
              style={{ color: "#a9b3c9" }}
              title="Refresh leaderboard"
            >
              <RefreshCw size={12} />
            </button>
          </div>
          <div className="grid gap-2 p-3"
               style={{ gridTemplateColumns: `repeat(${Math.min(leaderboard.length, 5)}, 1fr)` }}>
            {leaderboard.map((row, idx) => (
              <button
                key={row.job_id}
                type="button"
                data-testid={`karaoke-leaderboard-${row.job_id}`}
                onClick={() => setShareJob(row)}
                className="text-left rounded-lg border p-3 transition hover:-translate-y-[1px]"
                style={{
                  background: idx === 0 ? "rgba(255,138,31,0.10)" : "#1a1e2a",
                  borderColor: idx === 0 ? "rgba(255,138,31,0.45)" : "#232632",
                }}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="font-mono font-bold" style={{
                    color: idx === 0 ? "#FF8A1F" : "#a9b3c9",
                    fontSize: 18,
                  }}>#{idx + 1}</span>
                  {idx === 0 && <Flame size={14} style={{ color: "#FF8A1F" }} />}
                </div>
                <div className="text-white truncate mb-1" style={{ fontSize: 13, fontWeight: 600 }}>
                  {row.source_label || "Untitled"}
                </div>
                <div className="flex items-center gap-2 type-caption text-neutral-400">
                  <span className="inline-flex items-center gap-1">
                    <Share2 size={10} /> {row.share_count}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Play size={10} /> {row.play_count}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Source tab bar ────────────────────────────────── */}
      <div data-testid="karaoke-tab-bar"
           className="mb-4 border-b flex items-center gap-6 overflow-x-auto"
           style={{ borderColor: "#232632" }}>
        {SOURCE_TABS.map((t) => {
          const isActive = tab === t.id;
          const Icon = t.icon;
          const count = tabCounts[t.id] ?? 0;
          return (
            <button
              key={t.id}
              type="button"
              data-testid={`karaoke-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className="pb-3 -mb-px transition flex items-center gap-2 whitespace-nowrap"
              style={{
                color: isActive ? "#ffffff" : "#a9b3c9",
                borderBottom: isActive ? "2px solid #FF8A1F" : "2px solid transparent",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              <Icon size={14} />
              {t.label}
              <span className="rounded-full px-1.5 py-0.5 text-[10px]"
                    style={{ background: "#232632", color: isActive ? "#FF8A1F" : "#6c7180" }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Search + refresh ──────────────────────────────── */}
      <div className="flex items-center gap-2 mb-4">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: "#6c7180" }} />
          <input
            data-testid="karaoke-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search tracks by name or genre…"
            className="w-full rounded-lg pl-9 pr-3 py-2 outline-none"
            style={{ background: "#1a1e2a", border: "1px solid #232632", color: "#e6e9f0", fontSize: 13 }}
          />
        </div>
        <button data-testid="karaoke-refresh"
                onClick={() => { loadAssets(); loadJobs(); }}
                className="rounded-lg px-3 py-2"
                style={{ background: "#1a1e2a", border: "1px solid #232632", color: "#c4c9d4" }}>
          <RefreshCw size={14} className={(loadingAssets || loadingJobs) ? "animate-spin" : ""} />
        </button>
      </div>

      {/* ── Source picker grid ───────────────────────────── */}
      <div data-testid="karaoke-source-grid"
           className="grid gap-3 mb-8"
           style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
        {loadingAssets && filteredAssets.length === 0 ? (
          <div className="col-span-full text-center py-10 type-caption text-neutral-500">
            <Loader2 className="mx-auto mb-2 animate-spin" size={18} />
            Loading your audio library…
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="col-span-full rounded-xl border p-8 text-center"
               style={{ background: "#161923", borderColor: "#232632" }}>
            <FileMusic className="mx-auto mb-3" size={22} style={{ color: "#FF8A1F" }} />
            <div className="text-white mb-1" style={{ fontSize: 14, fontWeight: 600 }}>
              {tab === "all"
                ? "No karaoke-ready audio yet"
                : `Nothing in ${SOURCE_TABS.find((t) => t.id === tab)?.label} yet`}
            </div>
            <div className="type-caption text-neutral-400 mb-4">
              Upload a fresh track above, or head to Vocalist / Stem / Beds &amp; FX Studios to build one first.
            </div>
          </div>
        ) : (
          filteredAssets.map((a) => (
            <KaraokeSourceCard
              key={a.asset_id}
              asset={a}
              onSelect={setSelectedAsset}
            />
          ))
        )}
      </div>

      {/* ── Recent karaoke renders ───────────────────────── */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="text-white" style={{ fontSize: 15, fontWeight: 600 }}>
              Your karaoke renders
            </div>
            {reliability && reliability.terminal > 0 && (
              <ReliabilityChip data={reliability} />
            )}
          </div>
          <button onClick={() => { loadJobs(); loadReliability(); }} className="rounded p-1"
                  style={{ background: "#1a1e2a", border: "1px solid #232632", color: "#a9b3c9" }}
                  title="Refresh">
            <RefreshCw size={12} className={loadingJobs ? "animate-spin" : ""} />
          </button>
        </div>
        <div data-testid="karaoke-jobs-list" className="grid gap-3"
             style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}>
          {jobs.length === 0 ? (
            <div className="col-span-full rounded-xl border p-6 text-center type-caption text-neutral-500"
                 style={{ background: "#1a1e2a", borderColor: "#232632" }}>
              No renders yet. Pick a source above and generate your first karaoke video.
            </div>
          ) : (
            jobs.slice(0, 12).map((j) => (
              <KaraokeRenderCard key={j.job_id} job={j} onShare={setShareJob} onDelete={deleteJob} onRetry={retryJob} />
            ))
          )}
        </div>
      </div>

      {/* ── Compliance / tier note ───────────────────────── */}
      <div className="rounded-xl border p-4 flex items-start gap-3"
           style={{ background: "#161923", borderColor: "#232632" }}>
        <span className="rounded-full px-2 py-0.5 type-caption font-semibold uppercase tracking-wide"
              style={{ background: "rgba(255,138,31,0.15)", color: "#FF8A1F", fontSize: 10 }}>
          Pro Radio
        </span>
        <div className="type-caption text-neutral-400 flex-1">
          <b style={{ color: "#e6e9f0" }}>Studio Karaoke</b> uses your connected LALAL.AI key for
          stem separation (or falls back to the vocal-only rendering path on Pro Radio, Network,
          and Enterprise plans). Whisper transcription runs on the Emergent LLM key — no extra
          setup required.
        </div>
      </div>

      {/* ── Existing render modal + share modal ───────────── */}
      <KaraokeModal
        open={!!selectedAsset}
        asset={selectedAsset || undefined}
        onClose={() => setSelectedAsset(null)}
        onCreated={() => loadJobs()}
      />
      <KaraokeShareModal
        open={!!shareJob}
        jobId={shareJob?.job_id}
        sourceLabel={shareJob?.source_label || "Karaoke render"}
        defaultCaption={
          shareJob
            ? `🎤 ${shareJob.source_label || "Karaoke edit"}\n\nFresh karaoke drop from the studio.\n\n#Karaoke #Radio #BroadcastFlow`
            : ""
        }
        onClose={() => setShareJob(null)}
      />
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// Source card — tappable tile for each audio asset
// ────────────────────────────────────────────────────────────────────
function KaraokeSourceCard({ asset, onSelect }) {
  const initials = (asset.label || asset.name || "??")
    .split(/[^A-Za-z0-9]+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "").join("") || "AI";
  const kindLabel = {
    voice: "AI Vocal", bed: "Bed", jingle: "Jingle", sfx: "SFX", fx: "FX",
    generated: "AI Generated", mixdown: "Mixdown", opener: "Opener",
    closer: "Closer", ping: "Ping", impact: "Impact", music: "Music",
    library: "Library", imaging: "Imaging", commercial: "Commercial",
    podcast: "Podcast", ai_generate: "AI",
  }[asset.kind] || asset.kind || "Audio";

  return (
    <button
      type="button"
      data-testid={`karaoke-source-${asset.asset_id}`}
      onClick={() => onSelect(asset)}
      className="text-left rounded-xl border p-4 transition hover:-translate-y-[1px] group"
      style={{ background: "#1a1e2a", borderColor: "#232632" }}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="h-11 w-11 rounded-full flex items-center justify-center type-caption font-semibold shrink-0"
             style={{ background: "#232632", color: "#FF8A1F", fontSize: 13 }}>
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white truncate" style={{ fontSize: 14, fontWeight: 600 }}>
            {asset.label || asset.name || asset.asset_id}
          </div>
          <div className="type-caption text-neutral-400 truncate">
            {asset.genre || kindLabel}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="rounded px-1.5 py-0.5 type-caption"
              style={{ background: "rgba(255,138,31,0.14)", color: "#FF8A1F", fontSize: 10 }}>
          {kindLabel}
        </span>
        <span className="type-caption flex items-center gap-1 opacity-0 group-hover:opacity-100 transition"
              style={{ color: "#FF8A1F", fontSize: 11 }}>
          Open in Karaoke <ArrowRight size={12} />
        </span>
      </div>
    </button>
  );
}


// ────────────────────────────────────────────────────────────────────
// Render card — a past karaoke job with preview + share
// ────────────────────────────────────────────────────────────────────
function KaraokeRenderCard({ job, onShare, onDelete, onRetry }) {
  const navigate = useNavigate();
  const [sendingToPrompter, setSendingToPrompter] = useState(false);
  const isDone = job.status === "succeeded" && (job.video_url || job.video_asset_id);

  const sendToPrompter = async () => {
    setSendingToPrompter(true);
    try {
      const { data } = await api.post(`/karaoke-prompter/import/${job.job_id}`);
      toast.success(`"${data.title}" is in the Karaoke Prompter — word timing pre-synced`, {
        action: { label: "Open prompter", onClick: () => navigate("/dashboard/karaoke-prompter") },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send to the Karaoke Prompter");
    } finally { setSendingToPrompter(false); }
  };
  const isFailed = job.status === "failed";
  const isAutoRetrying = job.status === "auto_retrying";
  const isProcessing = !isDone && !isFailed && !isAutoRetrying;
  const videoUrl = job.video_url
    || (job.job_id ? `${API_BASE}/production/karaoke/${job.job_id}/video` : "");

  // Countdown to the next auto-retry (seconds) — recomputed each render.
  // We derive it from `next_auto_retry_at` rather than a live setInterval
  // so we stay lightweight; the ticker refreshes when the user hits the
  // manual refresh button (jobs get re-fetched anyway on a real refresh).
  let autoRetryInS = null;
  if (isAutoRetrying && job.next_auto_retry_at) {
    const delta = Math.round((new Date(job.next_auto_retry_at).getTime() - Date.now()) / 1000);
    autoRetryInS = Math.max(0, delta);
  }

  return (
    <div data-testid={`karaoke-render-${job.job_id}`}
         className="group rounded-xl border overflow-hidden relative"
         style={{ background: "#1a1e2a", borderColor: "#232632" }}>
      {/* Delete button — visible on hover, top-right corner. Failed
          renders always show it (nothing worth playing anyway). */}
      {onDelete && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(job); }}
          data-testid={`karaoke-delete-${job.job_id}`}
          title={isFailed ? "Delete failed render" : "Remove from library"}
          aria-label="Delete karaoke render"
          className={`absolute z-10 rounded-full flex items-center justify-center transition ${isFailed ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          style={{
            top: 8, right: 8, width: 28, height: 28,
            background: "rgba(15, 17, 23, 0.85)",
            border: "1px solid rgba(248, 113, 113, 0.4)",
            color: "#f87171",
            backdropFilter: "blur(4px)",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(248, 113, 113, 0.9)"; e.currentTarget.style.color = "#fff"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(15, 17, 23, 0.85)"; e.currentTarget.style.color = "#f87171"; }}
        >
          <Trash2 size={14} />
        </button>
      )}
      {isDone && videoUrl ? (
        <video src={videoUrl}
               controls
               preload="metadata"
               poster={job.thumbnail_url || undefined}
               className="w-full aspect-video bg-black"
               data-testid={`karaoke-render-video-${job.job_id}`} />
      ) : (
        <div className="aspect-video flex items-center justify-center"
             style={{ background: "#0f1117" }}>
          {isFailed ? (
            <div className="text-center px-4">
              <X className="mx-auto mb-1" size={22} style={{ color: "#f87171" }} />
              <div className="type-caption text-neutral-400 mb-3">
                {job.error || "Render failed"}
              </div>
              {onRetry && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onRetry(job); }}
                  data-testid={`karaoke-retry-${job.job_id}`}
                  title="Retry this render with the same source and settings"
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 type-caption font-medium text-white transition"
                  style={{
                    background: "linear-gradient(135deg, #FF8A1F 0%, #a78bfa 100%)",
                    fontSize: 11,
                    boxShadow: "0 2px 8px rgba(255, 138, 31, 0.35)",
                  }}
                >
                  <RotateCw size={11} />
                  Retry render
                </button>
              )}
              {job.retry_count > 0 && (
                <div className="type-caption text-neutral-600 mt-2" style={{ fontSize: 10 }}>
                  Attempt {job.retry_count + 1}
                </div>
              )}
            </div>
          ) : isAutoRetrying ? (
            <div className="text-center px-4"
                 data-testid={`karaoke-auto-retry-${job.job_id}`}>
              <RotateCw
                className="mx-auto mb-2 animate-spin"
                size={22}
                style={{ color: "#ffb84a", animationDuration: "2s" }}
              />
              <div className="type-caption mb-1" style={{ color: "#ffb84a", fontWeight: 600 }}>
                Auto-retrying{autoRetryInS != null ? ` in ~${autoRetryInS}s` : "…"}
              </div>
              <div className="type-caption text-neutral-500" style={{ fontSize: 10 }}>
                Transient error — retrying automatically
              </div>
            </div>
          ) : (
            <div className="text-center">
              <Loader2 className="mx-auto mb-2 animate-spin" size={20} style={{ color: "#FF8A1F" }} />
              <div className="type-caption text-neutral-400">
                {job.progress_step ? job.progress_step.replace(/_/g, " ") : "Processing…"}
              </div>
            </div>
          )}
        </div>
      )}
      <div className="p-3">
        <div className="text-white truncate mb-1" style={{ fontSize: 13, fontWeight: 600 }}>
          {job.source_label || "Untitled"}
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="type-caption text-neutral-500">
            {job.style || "gradient"} · {isDone ? "Ready" : (isFailed ? "Failed" : (isAutoRetrying ? "Auto-retrying" : "Rendering"))}
          </span>
          {isDone && (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={sendToPrompter}
                disabled={sendingToPrompter}
                data-testid={`karaoke-to-prompter-${job.job_id}`}
                title="Send this track's word-synced lyrics to the Karaoke Prompter"
                className="rounded-full px-3 py-1 type-caption font-medium transition disabled:opacity-50"
                style={{ background: "rgba(245, 166, 35, 0.12)", border: "1px solid rgba(245, 166, 35, 0.45)", color: "#f5a623", fontSize: 11 }}
              >
                {sendingToPrompter
                  ? <Loader2 size={11} className="inline mr-1 animate-spin" />
                  : <MicVocal size={11} className="inline mr-1" />} To Prompter
              </button>
              <button
                type="button"
                onClick={() => onShare(job)}
                data-testid={`karaoke-share-${job.job_id}`}
                className="rounded-full px-3 py-1 type-caption font-medium text-white transition"
                style={{ background: "linear-gradient(135deg, #FF8A1F 0%, #a78bfa 100%)", fontSize: 11 }}
              >
                <Share2 size={11} className="inline mr-1" /> Share
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



// ────────────────────────────────────────────────────────────────────
// Reliability chip (Update #157) — compact 24h health signal shown in
// the "Your karaoke renders" header. Reads the aggregate response from
// /api/production/karaoke/reliability and turns it into a single-line
// pill with three color-coded segments (green / amber / red).
// ────────────────────────────────────────────────────────────────────
function ReliabilityChip({ data }) {
  const { first_try_pct = 0, recovered_pct = 0, failed_pct = 0, terminal = 0, window_hours = 24 } = data || {};
  // Overall vibe drives the dot color: green (>=90% first-try),
  // amber (70-89%), red (<70%). Small visual signal so a glance at
  // the page tells the user whether the pipeline is behaving.
  const dotColor =
    first_try_pct >= 90 ? "#3ddc97" :
    first_try_pct >= 70 ? "#ffb84a" :
    "#f87171";

  const title = (
    `Last ${window_hours}h · ${terminal} render${terminal === 1 ? "" : "s"}\n` +
    `${first_try_pct}% first-try success\n` +
    `${recovered_pct}% recovered via retry\n` +
    `${failed_pct}% failed`
  );

  return (
    <div
      data-testid="karaoke-reliability-chip"
      title={title}
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1"
      style={{
        background: "#161923",
        border: "1px solid #262b3a",
        fontSize: 10,
        color: "#a9b3c9",
        fontFamily: "ui-monospace, monospace",
      }}
    >
      <span
        style={{
          width: 6, height: 6, borderRadius: 999,
          background: dotColor,
          boxShadow: `0 0 4px ${dotColor}88`,
        }}
      />
      <span data-testid="karaoke-reliability-first-try" style={{ color: "#3ddc97" }}>{first_try_pct}%</span>
      <span style={{ color: "#4a5060" }}>·</span>
      <span data-testid="karaoke-reliability-recovered" style={{ color: "#ffb84a" }}>↺ {recovered_pct}%</span>
      <span style={{ color: "#4a5060" }}>·</span>
      <span data-testid="karaoke-reliability-failed" style={{ color: "#f87171" }}>✕ {failed_pct}%</span>
      <span style={{ color: "#4a5060" }}>·</span>
      <span style={{ color: "#6b7280" }}>{window_hours}h</span>
    </div>
  );
}
